import axios from "axios";
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { MdRemoveRedEye } from "react-icons/md";
import { FaRegCalendarAlt, FaRegClock, FaRegCommentDots } from "react-icons/fa";
import ReactPaginate from "react-paginate";

const ITEMS_PER_PAGE = 6;

function CardArtikel({ selectedCategory, searchQuery }) {
  const [posts, setPosts] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`/api_w/posts`, {
          headers: {
            db_name: process.env.REACT_APP_DB_NAME,
          },
        });
        const sortedPosts = response.data.data.sort(
          (a, b) => new Date(b.date) - new Date(a.date)
        );
        setPosts(sortedPosts);
        setCurrentPage(0);
      } catch (error) {
        toast.error(error.response?.data?.message || error.message);
      }
    };

    fetchPosts();
  }, []);

  // Filter posts berdasarkan kategori dan search query
  const filteredPosts = posts.filter((post) => {
    const matchCategory = selectedCategory
      ? post.category_id === parseInt(selectedCategory)
      : true;

    const matchSearch = searchQuery
      ? post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        post.description.toLowerCase().includes(searchQuery.toLowerCase())
      : true;

    return matchCategory && matchSearch;
  });

  // Pagination
  const pageCount = Math.ceil(filteredPosts.length / ITEMS_PER_PAGE);
  const offset = currentPage * ITEMS_PER_PAGE;
  const currentPosts = filteredPosts.slice(offset, offset + ITEMS_PER_PAGE);

  const handlePageClick = (event) => {
    setCurrentPage(event.selected);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const formatDate = (dateString) => {
    const options = { year: "numeric", month: "long", day: "numeric" };
    return new Date(dateString).toLocaleDateString("id-ID", options);
  };

  const formatTime = (dateString) => {
    const options = { hour: "2-digit", minute: "2-digit" };
    return new Date(dateString).toLocaleTimeString("id-ID", options);
  };

  const truncateText = (text, maxLength) => {
    if (text.length > maxLength) {
      return text.slice(0, maxLength) + "...";
    }
    return text;
  };

  const stripHtmlTags = (html) => {
    const temp = document.createElement("div");
    temp.innerHTML = html;
    return temp.textContent || temp.innerText || "";
  };

  return (
    <div>
      {/* Posts List */}
      <div className="space-y-8">
        {currentPosts.length > 0 ? (
          currentPosts.map((post) => (
            <article
              key={post.id}
              className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:shadow-xl hover:border-green-200 transition-all duration-500"
            >
              {/* Image */}
              <div className="relative h-[250px] md:h-[320px] overflow-hidden bg-gray-200">
                <img
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  src={post.image}
                  alt={post.title}
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
              </div>

              {/* Content */}
              <div className="p-6 md:p-8">
                {/* Meta info */}
                <div className="flex flex-wrap items-center gap-3 mb-4 text-sm text-gray-500">
                  <span className="inline-flex items-center gap-1.5 bg-green-50 text-green-700 px-3 py-1 rounded-full text-xs font-medium">
                    <FaRegCalendarAlt className="w-3 h-3" />
                    {formatDate(post.date)}
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-xs">
                    <FaRegClock className="w-3.5 h-3.5 text-gray-400" />
                    {formatTime(post.date)} WIB
                  </span>
                  {post.views && (
                    <span className="inline-flex items-center gap-1.5 text-xs">
                      <MdRemoveRedEye className="w-3.5 h-3.5 text-gray-400" />
                      {post.views} views
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1.5 text-xs">
                    <FaRegCommentDots className="w-3.5 h-3.5 text-gray-400" />
                    0 Komentar
                  </span>
                </div>

                {/* Title */}
                <Link to={`/berita/${post.slug}`}>
                  <h2 className="text-xl md:text-2xl font-bold text-gray-800 group-hover:text-green-700 transition-colors duration-300 mb-3 leading-tight line-clamp-2">
                    {post.title}
                  </h2>
                </Link>

                {/* Description */}
                <p className="text-gray-600 leading-relaxed text-sm md:text-base mb-5 line-clamp-3">
                  {truncateText(stripHtmlTags(post.description), 200)}
                </p>

                {/* Read more */}
                <Link
                  to={`/berita/${post.slug}`}
                  className="inline-flex items-center gap-2 text-green-700 font-semibold text-sm hover:text-green-800 group/btn transition-colors duration-200"
                >
                  Baca Selengkapnya
                  <svg
                    className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform duration-200"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </Link>
              </div>
            </article>
          ))
        ) : (
          <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
            <p className="text-gray-500 text-lg">Tidak ada berita yang ditemukan</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pageCount > 1 && (
        <div className="mt-12 flex justify-center">
          <ReactPaginate
            previousLabel={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            }
            nextLabel={
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            }
            breakLabel="..."
            pageCount={pageCount}
            marginPagesDisplayed={1}
            pageRangeDisplayed={3}
            onPageChange={handlePageClick}
            forcePage={currentPage}
            containerClassName="flex items-center gap-1"
            pageClassName="w-10 h-10 flex items-center justify-center rounded-xl text-sm font-medium text-gray-600 hover:bg-green-50 hover:text-green-700 transition-colors duration-200"
            pageLinkClassName="w-full h-full flex items-center justify-center"
            activeClassName="!bg-green-600 !text-white shadow-lg shadow-green-600/30 hover:!bg-green-700 hover:!text-white"
            previousClassName="w-10 h-10 flex items-center justify-center rounded-xl text-gray-500 hover:bg-green-50 hover:text-green-700 transition-colors duration-200"
            previousLinkClassName="w-full h-full flex items-center justify-center"
            nextClassName="w-10 h-10 flex items-center justify-center rounded-xl text-gray-500 hover:bg-green-50 hover:text-green-700 transition-colors duration-200"
            nextLinkClassName="w-full h-full flex items-center justify-center"
            breakClassName="w-10 h-10 flex items-center justify-center text-gray-400"
            disabledClassName="opacity-30 cursor-not-allowed hover:bg-transparent"
          />
        </div>
      )}
    </div>
  );
}

export default CardArtikel;
